import { FUNKIT_CONNECT_SUPPORTED_CHAINS_INFO_LIST } from '@funkit/chains'
import {
  AddressZero,
  Auth,
  FunWallet,
  configureEnvironment,
  getEnvOptions,
} from '@funkit/core'
import {
  ChainNotConfiguredError,
  type CreateConnectorFn,
  createConnector,
  normalizeChainId,
} from '@wagmi/core'
import {
  type Address,
  type Chain,
  SwitchChainError,
  UserRejectedRequestError,
  numberToHex,
} from 'viem'

const ERROR_PREFIX = 'FunkitWagmiConnectorError'

const ConnectorEvent = {
  change: 'change',
  disconnect: 'disconnect',
  error: 'error',
  message: 'message',
  accountsChanged: 'accountsChanged',
  chainChanged: 'chainChanged',
} as const

funkitWagmiConnectorWallet.type = 'funkitWagmiConnectorWallet' as const

export function funkitWagmiConnectorWallet() {
  // Wagmi v2 Requirements
  // biome-ignore lint/suspicious/noExplicitAny: <intentional any>
  type Provider = any
  type Properties = Record<string, unknown>
  type StorageItem = { 'funkitConnectWagmi.disconnected': true }

  // Funwallet Requirements
  // Connection info
  let _privyProvider: Provider
  let _fwIndex: number | undefined
  // FunWallet info
  let _fwAuth: Auth | null = null
  let _funWallet: FunWallet | null = null

  return createConnector<Provider, Properties, StorageItem>(
    ({ chains = FUNKIT_CONNECT_SUPPORTED_CHAINS_INFO_LIST, emitter }) =>
      ({
        id: 'funkitConnector',
        name: 'Funkit Connector',
        type: funkitWagmiConnectorWallet.type,
        /**===============================
         * Custom Funkit Connector Methods
         *================================*/
        getFunWallet(): FunWallet | null {
          return _funWallet
        },
        getAuth(): Auth | null {
          return _fwAuth
        },
        getInternalConfigs() {
          return {
            _privyProvider,
            _fwIndex,
          }
        },
        async beforeConnect({
          provider,
          index,
        }: {
          provider: Provider
          index: number
        }) {
          if (!provider) {
            throw new Error(
              `${ERROR_PREFIX}:beforeConnect - Missing provider param`,
            )
          }

          if (index == null || typeof index !== 'number') {
            throw new Error(
              `${ERROR_PREFIX}:beforeConnect - Invalid index param`,
            )
          }

          // Setup the global variables for usage in connect()
          _privyProvider = provider
          _fwIndex = index
        },
        /**=========================
         * Wagmi Connector Methods
         *=========================*/
        // Optional function for running when the connector is first created
        async setup(): Promise<void> {
          return
        },
        // Function for connecting the connector.
        async connect({ chainId }: { chainId: number | undefined }) {
          const provider = await this.getProvider()
          if (!provider) {
            throw new Error(
              `${ERROR_PREFIX}: Ensure beforeConnect() is called before connect()`,
            )
          }

          // Attach listeners
          provider.on(ConnectorEvent.accountsChanged, this.onAccountsChanged)
          provider.on(ConnectorEvent.disconnect, this.onDisconnect)
          provider.on(ConnectorEvent.chainChanged, this.onChainChanged)
          emitter.emit(ConnectorEvent.message, { type: 'connecting' })

          // Create the funWallet auth and wallet.
          _fwAuth = new Auth({ provider })
          const walletUniqueId = await _fwAuth.getWalletUniqueId(_fwIndex)
          const userId: Address = await _fwAuth.getUserId()
          _funWallet = new FunWallet({
            users: [{ userId }],
            uniqueId: walletUniqueId,
          })

          // Fetch updated information
          const [newAccountAddress, curChainId] = await Promise.all([
            _funWallet.getAddress(),
            this.getChainId(),
          ])
          let currentChainId = curChainId

          // Switch to chain if provided
          if (chainId && currentChainId !== chainId) {
            const chain = await this.switchChain?.({ chainId }).catch(
              (error) => {
                if (error.code === UserRejectedRequestError.code) {
                  throw error
                }
                return { id: currentChainId }
              },
            )
            currentChainId = chain?.id ?? currentChainId
          }

          return {
            accounts: [newAccountAddress],
            chainId: currentChainId,
          }
        },
        // Function for disconnecting the connector.
        async disconnect(): Promise<void> {
          const provider: Provider = await this.getProvider()
          provider.removeListener(
            ConnectorEvent.accountsChanged,
            this.onAccountsChanged,
          )
          provider.removeListener(
            ConnectorEvent.disconnect,
            this.onDisconnect.bind(this),
          )
          provider.removeListener(
            ConnectorEvent.chainChanged,
            this.onDisconnect,
          )

          _funWallet = null
          _fwAuth = null
        },
        // Function that returns the connected accounts for the connector.
        async getAccounts(): Promise<Address[]> {
          if (_funWallet == null) {
            return [AddressZero]
          }
          return [await _funWallet.getAddress()]
        },
        // Function that returns the connected chain ID for the connector.
        async getChainId(): Promise<number> {
          // TODO: Decide between method (read from provider or funkit env)
          const provider: Provider = await this.getProvider()
          const chainId =
            provider.chainId ??
            (await provider?.request({ method: 'eth_chainId' }))
          return normalizeChainId(chainId)
        },
        // Function that returns the underlying provider interface for internal use throughout the connector.
        async getProvider(): Promise<Provider> {
          return _privyProvider
        },
        // Function that returns whether the connector has connected previously and is still authorized.
        async isAuthorized(): Promise<boolean> {
          try {
            const accounts = await this.getAccounts()
            return _funWallet != null && _fwAuth != null && !!accounts.length
          } catch {
            return false
          }
        },
        // Optional function for switching the connector's active chain
        async switchChain({ chainId }: { chainId: number }): Promise<Chain> {
          try {
            const newChain = chains.find((chain) => chain?.id === chainId)
            if (!newChain) {
              throw new SwitchChainError(new ChainNotConfiguredError())
            }

            // Reconfigure fun env
            const curEnv = getEnvOptions()
            await configureEnvironment({
              ...curEnv,
              chain: chainId,
            })

            // Switch chain in underlying provider
            const provider: Provider = await this.getProvider()
            const chainIdHex = numberToHex(newChain.id)
            await provider.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: chainIdHex }],
            })

            // Using the updated provider, recreate the funWallet auth and wallet.
            _fwAuth = new Auth({ provider })
            const walletUniqueId = await _fwAuth.getWalletUniqueId(_fwIndex)
            const userId: Address = await _fwAuth.getUserId()
            _funWallet = new FunWallet({
              users: [{ userId }],
              uniqueId: walletUniqueId,
            })
            // Emit accounts change to sync the state with upstream
            emitter.emit(ConnectorEvent.change, {
              chainId: newChain.id,
              accounts: await this.getAccounts(),
            })

            return newChain
          } catch (err) {
            console.error(`${ERROR_PREFIX}: Failed to switch chain`, err)
            throw err // Re-throw the error after logging
          }
        },
        /**=========================
         * Wagmi Connector Events
         *=========================*/
        // Function for subscribing to account changes internally in the connector.
        onAccountsChanged(): void {
          emitter.emit(ConnectorEvent.disconnect)
        },
        // Function for subscribing to chain changes internally in the connector.
        onChainChanged(chainId: string): void {
          const normalizedChainId = normalizeChainId(chainId)
          emitter.emit(ConnectorEvent.change, { chainId: normalizedChainId })
        },
        // Function for subscribing to disconnection events internally in the connector.
        onDisconnect(error?: Error): void {
          if (error) {
            console.error(`${ERROR_PREFIX}: Error during disconnection`, error)
          }
          emitter.emit(ConnectorEvent.disconnect)
        },
      }) as ReturnType<CreateConnectorFn<Provider, Properties, StorageItem>>,
  )
}
